import { decodeJWT } from '@/utils/jwtUtils';
import { postApiAuthLogin } from '@/views/auth/hooks/usePostApiAuthLogin';
import { postApiAuthLogout } from '@/views/auth/hooks/usePostApiAuthLogout';
import { useAuthorizationStore } from '@/stores/authorization';
import { queryClient } from '@/lib/queryClient';
import { resolveApiBaseUrl } from '@/utils/resolveApiBaseUrl';
import { create } from 'zustand';
import axios from 'axios';

import { persist, createJSONStorage, devtools } from 'zustand/middleware';

export interface EstablishmentContextOption {
  establishment_config_id: string;
  establishment_id: string;
  workspace_id: string;
  name: string;
  is_config_admin: boolean;
}

export interface User {
  id: string;
  auth_scope?: string;
  professional_id?: string;
  is_config_admin: boolean;
  tel: string;
  role: string;
  role_id?: string;
  can_approve_company_documents_directly: boolean;
  can_approve_establishment_documents_directly: boolean;
  can_approve_documents_directly: boolean;
  can_manage_records: boolean;
  user: string;
  name: string;
  email: string;
  establishment_config_id: string;
  establishment_config_ids: string[];
  context_type?:
    | 'establishment'
    | 'multi_establishment'
    | 'all_establishments'
    | string;
  establishment_id: string;
  establishment_workspace_id: string;
  company_id: string;
  company_config_id: string;
  is_company_admin: boolean;
  allow_document_header_export_choice: boolean;
  establishments: EstablishmentContextOption[];
}

export const canUseMultiEstablishmentContext = (
  user?: Pick<User, 'establishments'> | null,
) => (user?.establishments?.length ?? 0) > 1;

export interface UserGroup {
  id: string;
  name: string;
  permissions: Permission[];
}

export interface Permission {
  id: string;
  name: string;
  resource: string;
  action: string;
}

interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoggingOut: boolean;
  isLoading: boolean;
  isSelectingContext: boolean;
  contextSelectionRequired: boolean;
  contextSelectionRedirectRoute: string | null;
  selectedEstablishmentConfigId: string | null;
  selectedEstablishmentConfigIds: string[];
  login: (username: string, password: string) => Promise<void>;
  selectEstablishmentContext: (
    establishmentConfigId: string | string[],
  ) => Promise<void>;
  requireContextSelection: (redirectRoute?: string | null) => void;
  clearSession: () => void;
  expireSession: () => void;
  logout: () => void;
  setLoading: (loading: boolean) => void;
}

const environment = import.meta.env.VITE_APP_ENV;
const BASE_URL = resolveApiBaseUrl();

const normalizeEstablishments = (
  decoded: Record<string, any>,
): EstablishmentContextOption[] => {
  const list = Array.isArray(decoded.establishments)
    ? decoded.establishments
    : [];

  return list
    .filter((item) => item && item.establishment_config_id)
    .map((item) => ({
      establishment_config_id: String(item.establishment_config_id),
      establishment_id: String(item.establishment_id ?? ''),
      workspace_id: String(item.workspace_id ?? ''),
      name: String(item.name ?? ''),
      is_config_admin: Boolean(item.is_config_admin),
    }));
};

const normalizeIDList = (value: unknown): string[] => {
  if (!Array.isArray(value)) return [];
  return value
    .map((item) => String(item ?? ''))
    .filter((item) => item && item !== '0');
};

export const useAuthStore = create<AuthState>()(
  devtools(
    persist(
      (set, get) => ({
        user: null,
        token: null,
        isAuthenticated: false,
        isLoggingOut: false,
        isLoading: false,
        isSelectingContext: false,
        contextSelectionRequired: false,
        contextSelectionRedirectRoute: null,
        selectedEstablishmentConfigId: null,
        selectedEstablishmentConfigIds: [],

        login: async (username: string, password: string) => {
          set({ isLoading: true });
          try {
            const response = await postApiAuthLogin({
              user: username,
              password,
            });

            if (
              'data' in response &&
              response.data &&
              typeof response.data === 'object' &&
              'token' in response.data
            ) {
              const { token, permission_token } = response.data as {
                token: string;
                permission_token?: string;
              };

              if (!permission_token) {
                set({ isLoading: false });
                throw new Error(
                  'Login failed: permission_token ausente na resposta',
                );
              }
              useAuthorizationStore
                .getState()
                .setPermissionToken(permission_token);
              const decoded = decodeJWT(token);
              const establishments = decoded
                ? normalizeEstablishments(decoded)
                : [];

              const user: User | null = decoded
                ? {
                    id: decoded.id || '',
                    auth_scope: decoded.auth_scope || '',
                    professional_id: decoded.professional_id
                      ? String(decoded.professional_id)
                      : '',
                    is_config_admin: decoded.is_config_admin || false,
                    tel: decoded.tel || '',
                    role: decoded.role || '',
                    role_id: decoded.role_id || '',
                    can_approve_company_documents_directly:
                      decoded.can_approve_company_documents_directly ??
                      [1, 8].includes(Number(decoded.role_id)),
                    can_approve_establishment_documents_directly:
                      decoded.can_approve_establishment_documents_directly ??
                      decoded.can_approve_documents_directly ??
                      [1, 8].includes(Number(decoded.role_id)),
                    can_approve_documents_directly:
                      decoded.can_approve_documents_directly ??
                      decoded.can_approve_establishment_documents_directly ??
                      [1, 8].includes(Number(decoded.role_id)),
                    can_manage_records: decoded.can_manage_records ?? false,
                    user: decoded.user || '',
                    name: decoded.name || '',
                    email: decoded.email || '',
                    establishment_config_id:
                      decoded.establishment_config_id != null
                        ? String(decoded.establishment_config_id)
                        : '',
                    establishment_config_ids:
                      normalizeIDList(decoded.establishment_config_ids).length >
                      0
                        ? normalizeIDList(decoded.establishment_config_ids)
                        : decoded.establishment_config_id
                          ? [String(decoded.establishment_config_id)]
                          : [],
                    context_type: decoded.context_type || '',
                    establishment_id: decoded.establishment_id || '',
                    establishment_workspace_id:
                      decoded.establishment_config?.workspace_id ||
                      decoded.establishment_workspace_id ||
                      establishments[0]?.workspace_id ||
                      '',
                    company_id: decoded.company_id || '',
                    company_config_id: decoded.company_config_id || '',
                    is_company_admin: decoded.is_company_admin || false,
                    allow_document_header_export_choice:
                      decoded.allow_document_header_export_choice || false,
                    establishments,
                  }
                : null;

              const requiresSelection = canUseMultiEstablishmentContext(user);

              set({
                user,
                token,
                isAuthenticated: !!token,
                isLoggingOut: false,
                isLoading: false,
                contextSelectionRequired: requiresSelection,
                contextSelectionRedirectRoute: null,
                selectedEstablishmentConfigId: requiresSelection
                  ? null
                  : user?.establishment_config_id || null,
                selectedEstablishmentConfigIds: requiresSelection
                  ? []
                  : user?.establishment_config_ids || [],
              });
            } else {
              set({ isLoading: false });
              throw new Error('Login failed: invalid response');
            }
          } catch (error) {
            set({ isLoading: false });
            throw error;
          }
        },

        selectEstablishmentContext: async (
          establishmentConfigId: string | string[],
        ) => {
          const token = get().token;
          const user = get().user;
          if (!token || !user) {
            throw new Error('user is not authenticated');
          }

          const selectedIDs = Array.isArray(establishmentConfigId)
            ? establishmentConfigId.filter(Boolean)
            : establishmentConfigId === 'all'
              ? user.establishments.map((item) => item.establishment_config_id)
              : [establishmentConfigId].filter(Boolean);

          set({ isSelectingContext: true });
          try {
            const response = await axios.post(
              `${BASE_URL}/api/user/context/establishment`,
              {
                context_type:
                  Array.isArray(establishmentConfigId) ||
                  establishmentConfigId === 'all'
                    ? 'multi_establishment'
                    : 'establishment',
                establishment_config_id: Number(selectedIDs[0]),
                establishment_config_ids: selectedIDs.map(Number),
              },
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                },
              },
            );

            const selected = response.data?.selected_establishment;
            if (!selected) {
              throw new Error('invalid establishment context response');
            }
            const responseIDs = normalizeIDList(
              response.data?.establishment_config_ids,
            );

            const newPermissionToken = response.data?.permission_token;
            if (newPermissionToken) {
              useAuthorizationStore
                .getState()
                .setPermissionToken(newPermissionToken);
            }

            const selectedId = String(selected.establishment_config_id);
            const selectedEstablishmentId = String(
              selected.establishment_id ?? '',
            );
            const selectedWorkspaceId = String(
              selected.workspace_id ??
                user.establishments.find(
                  (item) => item.establishment_config_id === selectedId,
                )?.workspace_id ??
                user.establishment_workspace_id ??
                '',
            );
            const selectedIsConfigAdmin = Boolean(selected.is_config_admin);
            const allowDocumentHeaderExportChoice = Boolean(
              response.data?.allow_document_header_export_choice,
            );
            const finalIDs =
              responseIDs.length > 0 ? responseIDs : [selectedId];

            set((state) => ({
              user: state.user
                ? {
                    ...state.user,
                    establishment_config_id: selectedId,
                    establishment_config_ids: finalIDs,
                    context_type:
                      response.data?.context_type ||
                      (finalIDs.length > 1
                        ? 'multi_establishment'
                        : 'establishment'),
                    establishment_id: selectedEstablishmentId,
                    establishment_workspace_id: selectedWorkspaceId,
                    is_config_admin: selectedIsConfigAdmin,
                    allow_document_header_export_choice:
                      allowDocumentHeaderExportChoice,
                  }
                : null,
              selectedEstablishmentConfigId: selectedId,
              selectedEstablishmentConfigIds: finalIDs,
              contextSelectionRequired: false,
              contextSelectionRedirectRoute: null,
              isSelectingContext: false,
            }));

            await queryClient.invalidateQueries({ refetchType: 'active' });
          } catch (error: any) {
            set({ isSelectingContext: false });
            if (error?.response?.status === 401) {
              get().expireSession();
            }
            throw error;
          }
        },

        requireContextSelection: (redirectRoute = null) => {
          set((state) => {
            const canUseContext = canUseMultiEstablishmentContext(state.user);
            const safeRedirectRoute =
              typeof redirectRoute === 'string' ? redirectRoute : null;

            if (!canUseContext) {
              return {
                contextSelectionRequired: false,
                contextSelectionRedirectRoute: null,
                selectedEstablishmentConfigId:
                  state.selectedEstablishmentConfigId,
                selectedEstablishmentConfigIds:
                  state.selectedEstablishmentConfigIds,
                user: state.user,
              };
            }

            return {
              contextSelectionRequired: true,
              contextSelectionRedirectRoute: safeRedirectRoute,
              selectedEstablishmentConfigId:
                state.selectedEstablishmentConfigId,
              selectedEstablishmentConfigIds:
                state.selectedEstablishmentConfigIds,
              user: state.user,
            };
          });
        },

        clearSession: () => {
          useAuthorizationStore.getState().clearAuthorization();
          queryClient.clear();
          set({
            user: null,
            token: null,
            isAuthenticated: false,
            isLoading: false,
            isSelectingContext: false,
            contextSelectionRequired: false,
            contextSelectionRedirectRoute: null,
            selectedEstablishmentConfigId: null,
            selectedEstablishmentConfigIds: [],
          });
          localStorage.removeItem('auth-storage');
          localStorage.removeItem('authorization-storage');
        },

        expireSession: () => {
          get().clearSession();
          if (window.location.pathname !== '/session/login') {
            window.location.replace('/session/login');
          }
        },

        logout: () => {
          const token = get().token;
          const shouldFakeLogout = get().user?.auth_scope === 'professional';

          set({ isLoggingOut: true });
          get().clearSession();

          if (token && !shouldFakeLogout) {
            postApiAuthLogout({
              headers: { Authorization: `Bearer ${token}` },
            }).catch(() => {});
          } else if (!token) {
            postApiAuthLogout().catch(() => {});
          }

          if (window.location.pathname !== '/session/login') {
            window.location.replace('/session/login');
          }
        },

        setLoading: (loading: boolean) => {
          set({ isLoading: loading });
        },
      }),
      {
        name: 'auth-storage',
        storage: createJSONStorage(() => localStorage),
        partialize: (state) => ({
          user: state.user,
          token: state.token,
          isAuthenticated: state.isAuthenticated,
          contextSelectionRequired: state.contextSelectionRequired,
          contextSelectionRedirectRoute: state.contextSelectionRedirectRoute,
          selectedEstablishmentConfigId: state.selectedEstablishmentConfigId,
          selectedEstablishmentConfigIds: state.selectedEstablishmentConfigIds,
        }),
      },
    ),
    {
      store: 'auth', //devtools
      name: 'auth',
      enabled: environment == 'development', //devtools disable production
    },
  ),
);
